<script setup>
import { ref, computed, onMounted, onBeforeMount, getCurrentInstance, watch } from 'vue'
import store from '@/store'
import { HAZARD_URL } from '@/main'
import ReportControls from '@/components/ReportControls.vue'
import HazardReportBody from '@/components/hazard/HazardReportBody.vue'
import HazardReportToolbar from '@/components/hazard/HazardReportToolbar.vue'
import SimilarityOptionsMenu from './SimilarityOptionsMenu.vue'
import { defaultVTooltipProps } from '@/utils/defaultProps'
import TableCardLayout from '../report-page-layouts/TableCardLayout.vue'
import TableControls from '../report-page-layouts/TableControls.vue'

const options = ref(null)
const defaultOptions = ref(null)
const showThreshold = ref(false)
const fpTypes = ref([])
const confirmDateTime = ref(null)

const { proxy } = getCurrentInstance()
const getCheckedStructures = computed(() => store.getters.getCheckedStructures)
const hazardLoading = computed(() => store.getters.getHazardLoading)

const generationWarningText = computed(() => {
  let msg = ''
  if (options.value?.cts) {
    msg += `The Chemical Transformation option: ${options.value.cts} generation${options.value.cts > 1 ? 's' : ''} is presently selected\n`
  }
  if (options.value?.analogsSearchType) {
    msg += `The Search Option: ${options.value.analogsSearchType} is presently selected\n`
  }
  msg += 'This can take a lot of time for multiple chemicals. Proceed?'
  return msg
})

const hazardNames = computed(() =>
  [{ value: null, text: '' }].concat(store.getters.getHazardEndpoints.map((e) => e.name)),
)

const hasValidReportInputs = computed(() => Boolean(proxy.routeChemId || store.getters.getCheckedStructures?.length))
const hasHazardReport = computed(() => Boolean(proxy.report?.hazardChemicals?.length))
const canRecalculateReport = computed(() => hasHazardReport.value && hasValidReportInputs.value)

const reportControlsStatus = computed(() => {
  if (hazardLoading.value) return 'loading'
  if (hasHazardReport.value) return canRecalculateReport.value ? 'stale' : 'fresh'
  return hasValidReportInputs.value ? 'ready' : 'invalid'
})

const hasActiveConfirmation = () => {
  const ONE_HOUR_MS = 60 * 60 * 1000
  return confirmDateTime.value != null && new Date() - confirmDateTime.value < ONE_HOUR_MS
}

const calculateConfirm = computed(() => {
  if ((!options.value?.analogsSearchType && !options.value?.cts) || hasActiveConfirmation()) return null
  return {
    title: 'Generate report for chemicals in ChemCart',
    text: generationWarningText.value,
    htmlMessage: true,
  }
})

const updateConfirmDateTime = () => {
  confirmDateTime.value = new Date()
}

const onCalculateReport = () => {
  if (calculateConfirm.value) updateConfirmDateTime()
  store.dispatch('getHazardReport', { chemicals: getCheckedStructures.value, options: options.value })
}

const onFilesUploaded = (result) => store.commit('SET_HAZARD_REPORT', result)

const fillFpTypes = () => {
  fpTypes.value = [{ value: 'bingo', text: 'Bingo' }].concat(
    store.getters.toxprintsProfiles.map((p) => ({ value: p.name, text: p.description })),
  )
}
const onReset = () => {
  proxy.reportData = null
  proxy.selectedStructures = []
  proxy.error = null
  store.commit('RESET_HAZARD_REPORT')

  if (proxy.routeChemId) proxy.$router.push('/hazard')
}

watch(
  options,
  (val) => {
    store.commit('SET_HAZARD_OPTIONS', val)
  },
  { deep: true },
)

watch(confirmDateTime, (val) => store.commit('SET_HAZARD_CONFIRM_DATE_TIME', val))

onBeforeMount(() => {
  options.value = store.getters.getHazardOptions
  confirmDateTime.value = store.getters.getHazardConfirmDateTime
  defaultOptions.value = store.getters.getDefaultHazardOptions
})

const maybeOpenChemCart = () => {
  if (!hasHazardReport.value && !hasValidReportInputs.value) store.commit('SET_VIEW_STRUCTURES', true)
}

onMounted(() => {
  fillFpTypes()
  maybeOpenChemCart()
})
</script>

<script>
import HazardReportState from '@/components/hazard/HazardReportState'

export default {
  mixins: [HazardReportState],
}
</script>

<template>
  <div class="d-flex flex-column h-100">
    <TableCardLayout>
      <template #table-controls>
        <TableControls :loading="hazardLoading">
          <template #left>
            <div class="d-flex align-center gap-2 mr-auto">
              <div class="d-flex align-center gap-1">
                <div id="hazard-cts-select">
                  <v-select
                    v-model="options.cts"
                    style="max-width: 12rem"
                    label="Chemical Transformations"
                    hide-details
                    outlined
                    :items="[
                      { text: 'No Metabolites', value: null },
                      { text: '1 generation', value: 1 },
                      { text: '2 generations', value: 2 },
                      { text: '3 generations', value: 3 },
                      { text: '4 generations', value: 4 },
                    ]"
                    dense
                  />
                </div>

                <v-tooltip
                  bottom
                  v-bind="defaultVTooltipProps"
                  activator="#hazard-cts-select"
                >
                  <span>
                    Include CTS breakdown products for 'hydrolysis', 'abiotic_reduction' and 'human_biotransformation'
                    up to the selected generation
                  </span>
                </v-tooltip>

                <v-select
                  id="hazard-analogs-select"
                  v-model="options.analogsSearchType"
                  label="Analogs search type"
                  hide-details
                  style="max-width: 12rem"
                  outlined
                  :items="[
                    { text: 'No Analogs', value: null },
                    { text: 'SUBSTRUCTURE', value: 'SUBSTRUCTURE' },
                    { text: 'SIMILAR', value: 'SIMILAR' },
                  ]"
                  dense
                />

                <v-btn-toggle
                  v-if="options.analogsSearchType"
                  style="position: relative"
                  dense
                >
                  <SimilarityOptionsMenu
                    :options="options"
                    :default-options="defaultOptions"
                    :fp-types="fpTypes"
                    :hazard-name-options="hazardNames"
                  />
                </v-btn-toggle>
              </div>

              <ReportControls
                :status="reportControlsStatus"
                calculate-id="hazard-generate-button"
                clear-id="hazard-clear-button"
                calculate-label="Calculate"
                calculate-tooltip="Generate Hazard report for chemicals in ChemCart"
                calculate-aria-label="Generate Hazard report for chemicals in ChemCart"
                clear-aria-label="Clear Hazard Report"
                :calculate-confirm="calculateConfirm"
                :ui="{ group: 'mr-auto', calculate: 'primary' }"
                @calculate="onCalculateReport"
                @clear="onReset"
              />
            </div>
          </template>

          <template
            v-if="report"
            #right
          >
            <HazardReportToolbar
              :report="report"
              :error="error"
              :filters="filters"
              :allow-single-mode="allowSingleMode"
              :single-mode="singleMode"
              :show-images="showImages"
              :profiles="profiles"
              :hierarchy="getHazardCategoriesHierarchy"
              :checked-structures="getCheckedStructures"
              :downloading="downloading"
              :export-items="hazardExportItems"
              :selected-structures="selectedStructures"
              :records-for-transfer="recordsForTransfer"
              :hazard-url="HAZARD_URL"
              :default-v-tooltip-props="defaultVTooltipProps"
              @update:filters="filters = $event"
              @update:singleMode="singleMode = $event"
              @update:showImages="showImages = $event"
              @update:downloading="downloading = $event"
              @select-all="onSelectAllStructures"
              @clear-selection="onClearSelection"
              @report-saved="onReportSaved"
              @report-updated="onReportUpdated"
              @acl-updated="onAclUpdated"
            />
          </template>
        </TableControls>
      </template>

      <HazardReportBody
        v-if="hasHazardReport"
        :downloading="downloading"
        :filtered-report="filteredReport"
        :single-mode="singleMode"
        :filters="filters"
        :show-images="showImages"
        :selected-structures="selectedStructures"
        :chem-id="chemId"
        :on-check-click="onStructureCheckboxClick"
        @onFilterAdded="onFilterSelection"
        @showUnlikely-changed="updateShowUnlikely"
      />
    </TableCardLayout>
  </div>
</template>
